import React from 'react';
import { Treatment } from '@/types/components/whatIsYourConcern';
import InfoBox from './InfoBox';
import RichText from '@/components/ui/RichText/RichText';
import R2Image from '@/components/ui/R2Image';

type Props = {
  treatment: Treatment;
  isOpen: boolean;
  onToggle: () => void;
};

type LegacyImageTreatment = {
  imageUrl?: string;
};

type NewImageTreatment = {
  image?: {
    urlDesktop?: string;
    urlMobile?: string;
    alt?: string;
    className?: string;
    width?: number;
    height?: number;
  };
};

const getTreatmentImage = (treatment: Treatment) => {
  const item = treatment as Treatment &
    LegacyImageTreatment &
    NewImageTreatment;

  const imagePath =
    item.image?.urlDesktop || item.image?.urlMobile || item.imageUrl || '';

  const imageAlt =
    item.image?.alt || treatment?.heading?.content || 'Treatment';

  return {
    imagePath,
    imageAlt,
    imageClassName:
      item.image?.className ||
      'h-full w-full rounded-lg object-cover  overflow-hidden',
    imageWidth: item.image?.width || 362,
    imageHeight: item.image?.height || 281,
  };
};

const TreatmentAccordionItem = ({ treatment, isOpen, onToggle }: Props) => {
  const { imagePath, imageAlt, imageClassName, imageWidth, imageHeight } =
    getTreatmentImage(treatment);

  const durationDetail = treatment.details?.find((detail) =>
    detail.content.toLowerCase().includes('duration'),
  );

  const downtimeDetail = treatment.details?.find((detail) =>
    detail.content.toLowerCase().includes('downtime'),
  );

  const resultsDetail = treatment.details?.find((detail) =>
    detail.content.toLowerCase().includes('results'),
  );

  const getDetailValue = (detail: { content?: string }) => {
    if (!detail?.content) return '';

    const parts = detail.content.split('\n\n');

    return parts[parts.length - 1]?.trim() || '';
  };

  return (
    <div
      className={`rounded-2xl border transition-all duration-200 ${
        isOpen ? 'border-border-brand bg-white' : 'border-border-muted bg-white'
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="flex w-full items-center justify-between px-4 py-4 text-left"
      >
        <RichText
          styles={treatment.heading.styles}
          className={`text-[16px] leading-tight font-bold [&>p]:mb-0 ${
            isOpen ? 'text-theme-primary' : 'text-text-default'
          }`}
        >
          {treatment.heading.content}
        </RichText>

        <span
          className={`text-text-muted transition-transform duration-300 ${
            isOpen ? 'rotate-180' : ''
          }`}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5 7.5L10 12.5L15 7.5"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>

      <div className="overflow-hidden rounded-lg">
        <div className={isOpen ? 'block px-4 py-4' : 'hidden px-4 py-4'}>
          {imagePath ? (
            <R2Image
              path={imagePath}
              alt={imageAlt}
              width={imageWidth}
              height={imageHeight}
              className={imageClassName}
            />
          ) : null}

          <div className="pt-4">
            <RichText
              styles={treatment.heading.styles}
              className="text-theme-primary text-lg font-bold [&>p]:mb-0"
            >
              {treatment.heading.content}
            </RichText>

            {treatment.description?.content ? (
              <RichText
                styles={treatment.description.styles}
                className="text-text-placeholder mt-1 text-xs font-semibold [&>p]:mb-0"
              >
                {treatment.description.content}
              </RichText>
            ) : null}

            <div className="mt-3 grid min-w-0 grid-cols-3 gap-1.5 sm:gap-2">
              {durationDetail && (
                <InfoBox
                  label="Duration"
                  value={getDetailValue(durationDetail)}
                  icon={
                    <R2Image
                      path="/icons/duration.webp"
                      alt="Duration"
                      width={16}
                      height={16}
                      className="h-full w-full"
                    />
                  }
                />
              )}

              {downtimeDetail && (
                <InfoBox
                  label="Downtime"
                  value={getDetailValue(downtimeDetail)}
                  icon={
                    <R2Image
                      path="/icons/down-time.webp"
                      alt="Downtime"
                      width={16}
                      height={16}
                      className="h-full w-full"
                    />
                  }
                />
              )}

              {resultsDetail && (
                <InfoBox
                  label="Results"
                  value={getDetailValue(resultsDetail)}
                  icon={
                    <R2Image
                      path="/icons/skin.webp"
                      alt="Results"
                      width={16}
                      height={16}
                      className="h-full w-full"
                    />
                  }
                />
              )}
            </div>

            {treatment.frequency?.content ? (
              <RichText
                styles={treatment.frequency.styles}
                className="font-regular text-default-caption [&>p>b]:text-text-brand mt-4 text-[14px] [&>p]:mb-0 [&>p>b]:text-[16px] [&>p>b]:font-bold"
              >
                {treatment.frequency.content}
              </RichText>
            ) : null}

            {treatment.bestSuitedFor?.content ? (
              <RichText
                styles={treatment.bestSuitedFor.styles}
                className="font-regular text-default-caption [&>p>b]:text-text-brand mt-4 text-[14px] [&_li]:mb-1 [&_li]:marker:text-[18px] [&_ul]:list-disc [&_ul]:pl-5 [&>p]:mb-0 [&>p>b]:text-[16px] [&>p>b]:font-bold"
              >
                {treatment.bestSuitedFor.content}
              </RichText>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TreatmentAccordionItem;
